import type { FC } from 'react';

import { CustomButton } from 'components/header/style';
import { Button, TextInput, Tooltip, Typography } from 'enable-ui';

import {
  AddContent,
  DeleteComp,
  DropdownContent,
  HeaderContent,
  Item,
  ItemContainer,
  Options,
} from '../styles/dashboard';

import { ReactComponent as AddIcon } from 'assets/icons/add.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import { useBoolean } from 'hooks';
import { isEmpty } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';
import { PATH } from 'constants/path';

export const Header: FC<{
  selected: Record<string, string>;
  title?: string;
  placeholderSearch?: string;
  options?: any[];
  setSelected: (value: Record<string, string>) => void;
  onDelete: () => void;
  searchValueBySelected?: string;
  onAdd?: (id: string) => void;
  type: string;
  searchValue: string;
  onChangeSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeSearchBySelected?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hideDeleteAction?: boolean;
  onClickAddItem?: () => void;
  closeModal?: boolean;
}> = ({
  selected,
  title,
  placeholderSearch,
  options,
  setSelected,
  onDelete,
  searchValueBySelected,
  searchValue,
  onAdd,
  type,
  onChangeSearch,
  onChangeSearchBySelected,
  hideDeleteAction,
  onClickAddItem,
  closeModal = false,
}) => {
  const visible = useBoolean(false);

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  return (
    <div
      style={{
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <TextInput
        placeholder={`Search by ${type}`}
        value={searchValue}
        onChange={onChangeSearch}
        inputStyle={{
          backgroundColor: 'transparent',
          width: 400,
        }}
        prefix={<SearchIcon />}
        containerStyle={{
          maxWidth: 'auto',
          width: 400,
        }}
      />
      {Boolean(Object.keys(selected).length) && (
        <HeaderContent>
          <Typography
            style={{
              fontSize: 16,
              lineHeight: 'calc(22/16)',
              color: COLOR.surfaceNeutralBold['neutral-11'],
              padding: '0 12px',
              marginRight: 8,
              fontWeight: 400,
            }}
          >
            {Object.keys(selected).length} selected
          </Typography>
          {title && (
            <Tooltip
              maxWidth={440}
              bg="transparent"
              trigger="click"
              arrow={false}
              placement="bottom"
              content={
                <DropdownContent>
                  <TextInput
                    placeholder={placeholderSearch}
                    prefix={<SearchIcon />}
                    value={searchValueBySelected ?? ''}
                    onChange={onChangeSearchBySelected}
                  />
                  <Options>
                    {options?.length ? (
                      options?.map((el, index) => (
                        <ItemContainer className="flex">
                          <Item
                            key={index}
                            onClick={() => {
                              onAdd?.(el.id);

                              if (closeModal) {
                                visible.setValue(false);
                              }
                            }}
                          >
                            {el.name}
                          </Item>

                          <Typography
                            style={{
                              fontSize: 14,
                              lineHeight: 'calc(20/14)',
                              color: COLOR_CONFIG['neutral-text'],
                            }}
                          >
                            {el.totalLessons} lessons • {el.totalAssessments} assessment
                          </Typography>
                        </ItemContainer>
                      ))
                    ) : (
                      <Typography style={{ textAlign: 'center' }}>No data</Typography>
                    )}
                  </Options>
                </DropdownContent>
              }
              visible={visible.value}
              offset={[5, 25]}
              allowHTML
              interactive
              onClickOutside={() => visible.setValue(false)}
            >
              <AddContent onClick={() => visible.setValue((prev) => !prev)}>
                <AddIcon style={{ width: 20, height: 20 }} />
                <Typography
                  style={{
                    fontSize: 16,
                    lineHeight: 'calc(22/16)',
                    fontWeight: 600,
                    color: COLOR.surfaceNeutralBold['neutral-11'],
                    marginLeft: 6,
                  }}
                >
                  {title}
                </Typography>
              </AddContent>
            </Tooltip>
          )}
          {!hideDeleteAction && (
            <DeleteComp onClick={onDelete}>
              <DeleteIcon style={{ width: 20, height: 20 }} />
              <Typography
                style={{
                  fontSize: 16,
                  lineHeight: 'calc(22/16)',
                  fontWeight: 600,
                  color: COLOR_CONFIG.red,
                  marginLeft: 6,
                }}
              >
                Delete
              </Typography>
            </DeleteComp>
          )}

          <CloseIcon
            style={{ width: 24, height: 24, cursor: 'pointer' }}
            onClick={() => {
              visible.setValue(false);
              setSelected({});
            }}
          />
        </HeaderContent>
      )}

      {isInstitutionDetail && isEmpty(selected) && (
        <CustomButton>
          <Button
            icon={<AddIcon style={{ marginRight: 4, width: 20, height: 20 }} />}
            placementIcon="left"
            onClick={onClickAddItem}
          >
            Add
          </Button>
        </CustomButton>
      )}
    </div>
  );
};
